import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { TimeStamped } from '@shared/models/timestamped';
import { DateTime } from 'luxon';
import { Observable, catchError, map, of, share, startWith } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Service that provides functionality for setting/retrieving cached API responses or triggering
 * network request as needed.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiCacheService {
  private readonly CACHE_EXPIRATION_MINUTES = 3; // Time before refresh.
  private readonly PILOT_HISTORY_CACHE_MINUTES = 1440; // Pilot history/contact info doesn't need refreshed very often.
  private readonly MAX_CACHE_AGE_DAYS = 7; // Time before deletion.

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  /**
   * Returns a cached response immediately if one exists. If the cached response
   * has expired, a network request will also be initiated. If a postBody is provided,
   * POST will be used instead of GET.
   * @template T Response type
   * @param {string}   cacheId    The unique string used to identify this response.
   * @param {string}   url        The request url.
   * @param {unknown}  postBody  Body to submit with POST request.
   * @returns {Observable<RequestState<T>>}
   */
  request<T extends TimeStamped>(
    cacheId: string,
    url: string,
    postBody?: unknown,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);
    let cacheExpirationTime = this.CACHE_EXPIRATION_MINUTES;

    // Use longer cache expiration for data that rarely changes.
    if (
      url.includes('pilotflightdataservice') ||
      url.includes('userprofileservice') ||
      url.includes('memberinfoservice')
    ) {
      cacheExpirationTime = this.PILOT_HISTORY_CACHE_MINUTES;
    }

    if (cachedResponse) {
      if (
        cachedResponse.timestamp &&
        DateTime.fromISO(cachedResponse.timestamp, { zone: 'utc' }).diffNow(
          'minutes',
        ).minutes *
          -1 <=
          cacheExpirationTime
      ) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      }
    }

    // console.log(`Cached data expired. Refreshing ${cacheId}.`);
    // No/expired cache, make network request.
    if (postBody) {
      return this.forceRefreshPost<T>(cacheId, url, postBody);
    } else {
      return this.forceRefreshGet<T>(cacheId, url);
    }
  }

  /**
   * Disregards cache expiration and triggers API call. Still immediately returns cached value
   * while loading. Also returns cached value on failure.
   */
  forceRefreshGet<T extends TimeStamped>(
    cacheId: string,
    url: string,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);

    return this.http.get<T>(url).pipe(
      map((response) => {
        if (response) {
          this.cacheResponse(cacheId, response);
        }

        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          response,
        );
      }),
      catchError((err) => this.handleError(err, cachedResponse)),
      // tap(() => console.log('cachedResponse', cachedResponse)),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse,
        ),
      ),
      share(),
    );
  }

  /**
   * POST version of forceRefreshGet.
   */
  forceRefreshPost<T extends TimeStamped>(
    cacheId: string,
    url: string,
    postBody?: unknown,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);

    return this.http.post<T>(url, postBody).pipe(
      map((response) => {
        if (response) {
          // Workaround when the API response is just an array.
          if (Array.isArray(response)) {
            response.list = response[0];
            const newResponse = {
              timestamp: DateTime.now().toISO(),
              list: response,
            };
            this.cacheResponse(cacheId, newResponse);

            // Needed to make typescript happy.
            const newCachedResponse = this.getCached<T>(cacheId);

            return new RequestState<T>(
              RequestStatus.IsCompleteWithResponse,
              newCachedResponse,
            );
          } else {
            this.cacheResponse(cacheId, response);
          }
        }

        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          response,
        );
      }),
      catchError((err) => this.handleError(err, cachedResponse)),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse,
        ),
      ),
      share(),
    );
  }

  /** Clear local storage items that are older than the specified age. */
  clearOldCache(): void {
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      const item = localStorage.getItem(key);
      if (item) {
        try {
          const parsedItem: TimeStamped = JSON.parse(item);
          if (parsedItem.timestamp) {
            const age =
              DateTime.fromISO(parsedItem.timestamp, { zone: 'utc' }).diffNow(
                'days',
              ).days * -1;

            if (age > this.MAX_CACHE_AGE_DAYS) {
              console.log(`Removing ${key}: ${age.toFixed(0)}.`);
              localStorage.removeItem(key);
            }
          }
        } catch {
          continue;
        }
      }
    }
  }

  /**
   * Gets the cached response if found. Returns null if not found.
   * @template T Response type.
   * @param {string} cacheId Tnique string to identify response.
   * @returns {T | null}
   */
  private getCached<T>(cacheId: string): T | null {
    const cached = localStorage.getItem(cacheId);

    if (!cached) {
      return null;
    }

    return JSON.parse(cached) as T;
  }

  /** Saves response to cache. */
  private cacheResponse<T extends TimeStamped>(
    cacheId: string,
    response: T,
  ): void {
    response.timestamp = DateTime.now().toISO();
    localStorage.setItem(cacheId, JSON.stringify(response));
  }

  private handleError<T extends TimeStamped>(
    error: HttpErrorResponse,
    cachedResponse?: T | null,
  ): Observable<RequestState<T>> {
    if (error.status === 404) {
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      } else {
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else if (error.status === 401) {
      console.log('401 encountered, authenticating...');
      this.authService.authenticate();
    } else if (!window.navigator.onLine) {
      // Don't show errors if offline.
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      } else {
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else {
      // Server error.
      console.error(
        `Server error loading data. Status: ${error.status}. Message: `,
        error.error,
      );
    }

    if (cachedResponse) {
      return of(
        new RequestState<T>(
          RequestStatus.HasErrorWithCachedResponse,
          cachedResponse,
        ),
      );
    } else {
      return of(new RequestState<T>(RequestStatus.HasErrorNoResponse));
    }
  }
}
